import { updateDiscordUsername, getTeamMembers, db, getUserProfile, updateUserProfile } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import MyHeader from "../../components/common/MyHeader";
import { MdCancel } from "react-icons/md";
import { FaCheckCircle } from "react-icons/fa";
import { useLocation } from 'react-router-dom';
import { onSnapshot, doc } from 'firebase/firestore';
import GoBackButton from "./components/GoBackButton";
import { AiOutlineInfoCircle } from "react-icons/ai";
import { HomeHeader } from '../Home/HomeHeader';
import {
  PageWrapper, Container, Header, Title, Subtitle,
  Card, MemberRow, FinishWrapper, Footer, FooterText, FooterLink,
} from './components/setupStyles';

function DiscordSetUpPage() {
  const { user, groupMembers, groupInfo, setGroupMembers } = useAuth();
  const [editState, setEditState] = useState({});
  const [saving, setSaving] = useState(false);
  const [profileName, setProfileName] = useState('');
  const location = useLocation();
  const isHomeMode =
    location.state?.connectionMode === 'home' ||
    location.state?.settingsHeaderMode === 'home' ||
    !groupInfo?.groupId;
  const displayedMembers = isHomeMode
    ? [{
        email: user?.email || 'current-user',
        name: profileName || user?.displayName || user?.email || 'You',
        discord_username: editState[user?.email] ?? ''
      }]
    : groupMembers;

  useEffect(() => {
    if (!isHomeMode || !user?.email) return;

    let isActive = true;

    const loadProfile = async () => {
      try {
        const profile = await getUserProfile(user.email);
        if (!isActive) return;
        setProfileName(profile?.name || user.displayName || user.email);
        setEditState(prev => ({
          ...prev,
          [user.email]: profile?.discord || ''
        }));
      } catch (error) {
        console.error('Error loading profile for Discord setup:', error);
        if (!isActive) return;
        setProfileName(user.displayName || user.email);
      }
    };

    loadProfile();
    return () => {
      isActive = false;
    };
  }, [isHomeMode, user]);

  useEffect(() => {
    if (isHomeMode || !groupInfo?.groupId) return;

    const unsubscribe = onSnapshot(doc(db, "Groups", groupInfo.groupId), (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        const members = data?.members || [];
        setGroupMembers(members);
      }
    });

    return () => unsubscribe();
  }, [groupInfo?.groupId, isHomeMode, setGroupMembers]);

  const handleChange = (email, value) => {
    setEditState(prev => ({ ...prev, [email]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      if (isHomeMode) {
        const value = editState[user?.email] !== undefined && editState[user?.email] !== null
          ? String(editState[user.email]).trim()
          : "";
        await updateUserProfile(user.email, { discord: value });
        alert("Discord username updated.");
        return;
      }

      const updates = Object.entries(editState);

      if (updates.length > 0) {
        for (const [email, value] of updates) {
          await updateDiscordUsername(groupInfo.groupId, email, value !== undefined && value !== null ? String(value).trim() : "");
        }

        const membersData = await getTeamMembers(user, groupInfo.groupId);
        setGroupMembers(membersData.members);
        alert("Discord usernames updated.");
      } else {
        alert("No new usernames entered. Enter Discord usernames and click Save.");
      }
    } catch (err) {
      console.error("Save error:", err);
      alert("Something went wrong while saving.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {isHomeMode ? <HomeHeader text="Connections" link="/student/home" /> : <MyHeader />}
      <PageWrapper>
        <Container>
          <GoBackButton />
          <Header>
            <Title>{isHomeMode ? 'Connect Your Discord Username' : 'Connect Your Discord Usernames'}</Title>
            <Subtitle>
              {isHomeMode
                ? 'Add your Discord username (without #) to your personal profile.'
                : 'Enter each member’s Discord username (without #) to link it to your real name.'}
            </Subtitle>
          </Header>

          <WarningBox>
              <IconBadge>
                <AiOutlineInfoCircle size={20} />
              </IconBadge>
              <WarningText>
              {isHomeMode
                ? 'This saves the Discord username on your personal profile when you access settings from home.'
                : 'If a Discord username isn’t added, we won’t be able to match it to a real name. Only the Discord username will display.'}
              </WarningText>
          </WarningBox>

          <Card>
            <CardList>
              {displayedMembers.map((member) => (
                <MemberRow key={member.email}>
                  <CardHeader>
                    <UserName>{member.name}</UserName>
                    <StatusGroup>
                      {member.discord_username ? (
                        <>
                          <BaseIcon className="green"><FaCheckCircle /></BaseIcon>
                          <Username>{member.discord_username}</Username>
                        </>
                      ) : (
                        <>
                          <BaseIcon className="red"><MdCancel /></BaseIcon>
                          <Status>Missing</Status>
                        </>
                      )}
                    </StatusGroup>
                  </CardHeader>
                  <CardBody>
                    <Input
                      type="text"
                      placeholder="Enter Discord username"
                      value={editState[member.email] ?? (member.discord_username ?? '')}
                      onChange={(e) => handleChange(member.email, e.target.value)}
                    />
                  </CardBody>
                </MemberRow>
              ))}
            </CardList>
            <FinishWrapper>
              <FinishButton onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </FinishButton>
            </FinishWrapper>
          </Card>
          <Footer>
            <FooterText>
              Need help? Check out our{" "}
              <FooterLink
                href="https://docs.google.com/document/d/10ktlofLuFkFbs9ZEEs2jwpvycE6nz8KBXZyE4FJ8w40/edit?usp=sharing"
                target="_blank"
                rel="noopener noreferrer"
              >
                documentation
              </FooterLink>
            </FooterText>
          </Footer>
        </Container>
      </PageWrapper>
    </>
  );
}

export default DiscordSetUpPage;


const WarningBox = styled.div`
  background: #fff7e6;
  border: 1px solid #f5d08a;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
`;

const WarningText = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: #9a3412;
  line-height: 1.4;
`;

const IconBadge = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 999px;

  display: grid;
  place-items: center;

  border: none; 
  color: #9a3412;          
  flex-shrink: 0;
`;

const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const UserName = styled.span`
  font-size: 1.125rem;
  font-weight: 600;
  color: #374151;
`;

const Status = styled.span`
  color: #ef4444;
  font-size: 0.875rem;
  margin-left: 0.25rem;
`;

const CardBody = styled.div`
  display: flex;
  width: 100%;
`;

const Input = styled.input`
  flex-grow: 1;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  outline: none;
  transition: all 0.2s ease-in-out;

  &:focus {
    border-color: transparent;
    box-shadow: 0 0 0 2px #5865f2;
  }
`;

const BaseIcon = styled.div`
  font-size: 1.5rem;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  vertical-align: middle;

  &.red {
    color: #ef4444;
  }

  &.green {
    color: #22c55e;
  }
`;

const Username = styled.span`
  color: #15803d;
  font-size: 0.875rem;
  font-weight: 500;
  margin-left: 0.5rem;
`;

const StatusGroup = styled.div`
  display: flex;
  align-items: center;
  margin-left: 0.75rem;
`;


const FinishButton = styled.button`
  background-color: #5865f2;
  color: white;
  font-weight: 600;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 0.75rem;
  transition: background-color 0.15s ease-in-out;

  &:hover:not(:disabled) {
    background-color: #4752c4;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;
